// ════════════════════════════════════════════════════════════════
// EmptyState — labeled "EXAMPLE" preview shown when a screen has no
// real data yet. Makes the product feel complete and teaches value.
//
// Usage:
//   <EmptyState
//      icon="⚡"
//      title="No automations yet"
//      subtitle="Set rules that run on their own — like alerting you when stock runs low."
//      actionLabel="+ Create your first automation"
//      onAction={() => setShowModal(true)}
//      columns={["Trigger","Action","Status"]}
//      exampleRows={[
//        ["Stock below 5", "Send WhatsApp alert", "Active"],
//        ["New order", "Add packing task", "Active"],
//      ]}
//   />
// ════════════════════════════════════════════════════════════════

export default function EmptyState({
  icon = "📭",
  title = "Nothing here yet",
  subtitle = "",
  actionLabel = "",
  onAction = null,
  columns = [],
  exampleRows = [],
  exampleCards = null,   // optional: array of {label, value, sub} for card-style previews
}) {
  return (
    <div style={{ position: "relative" }}>
      {/* The example preview — visible but clearly a sample */}
      <div style={{ position: "relative", opacity: 0.55, pointerEvents: "none", userSelect: "none", filter: "grayscale(0.3)" }}>
        {/* "EXAMPLE" watermark ribbon */}
        <div style={{
          position: "absolute", top: 10, right: 10, zIndex: 2,
          background: "#1A3A5C", color: "#fff", fontSize: 10, fontWeight: 700,
          letterSpacing: "1.5px", padding: "4px 12px", borderRadius: 20, textTransform: "uppercase",
        }}>
          Example
        </div>

        {/* Card-style preview */}
        {exampleCards && (
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(exampleCards.length, 4)}, 1fr)`, gap: 10, marginBottom: 14 }}>
            {exampleCards.map((c, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: "14px 16px", borderLeft: "3px solid #F47920" }}>
                <div style={{ fontSize: 9, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase" }}>{c.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#1A3A5C" }}>{c.value}</div>
                {c.sub && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>{c.sub}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Table-style preview */}
        {columns.length > 0 && (
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {columns.map((col, i) => (
                    <th key={i} style={{ padding: "9px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "#9ca3af", borderBottom: "1px solid #e5e7eb" }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {exampleRows.map((row, ri) => (
                  <tr key={ri} style={{ background: ri % 2 ? "#fafafa" : "#fff" }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding: "10px 14px", borderBottom: "1px solid #f3f4f6", color: "#374151" }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* The call-to-action overlay */}
      <div style={{
        textAlign: "center",
        padding: "28px 24px 8px",
      }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>{icon}</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1A3A5C", marginBottom: 6 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, maxWidth: 440, margin: "0 auto 18px" }}>{subtitle}</div>}
        {actionLabel && onAction && (
          <button onClick={onAction} style={{
            background: "#F47920", color: "#fff", border: "none", borderRadius: 8,
            padding: "11px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            boxShadow: "0 4px 14px rgba(244,121,32,0.3)",
          }}>
            {actionLabel}
          </button>
        )}
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 14 }}>
          The table above is just an example to show you how this works.
        </div>
      </div>
    </div>
  );
}
